import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useUpdateFolderMutation } from "@/modules/mine";
import type { Folder } from "@/modules/mine/navigation/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";

type EditFolderResult = {
  action: "updated";
  folderId: string;
};

interface EditFolderProps extends DialogComponentProps<EditFolderResult> {
  folder: Folder;
}

const formSchema = z.object({
  name: z.string()
    .min(1, { message: "文件夹名称不能为空" })
    .max(50, { message: "文件夹名称不能超过50个字符" }),
  note: z.string().max(200, { message: "描述不能超过200个字符" }).optional(),
});

export function EditFolder({ close, resolve, folder }: EditFolderProps) {
  const updateFolderMutation = useUpdateFolderMutation();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: folder.name,
      note: folder.note ?? "",
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    updateFolderMutation.mutate(
      {
        id: folder.id,
        name: values.name.trim(),
        note: values.note?.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("文件夹更新成功");
          resolve({
            action: "updated",
            folderId: folder.id,
          });
        },
      },
    );
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">文件夹名称</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="请输入文件夹名称" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">描述</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="请输入描述" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={updateFolderMutation.isPending}
        >
          取消
        </Button>
        <Button
          type="button"
          onClick={form.handleSubmit(onSubmit)}
          disabled={updateFolderMutation.isPending}
        >
          确认
          {updateFolderMutation.isPending && <Loader2 className="animate-spin" />}
        </Button>
      </DialogFooter>
    </>
  );
}
